import { useMemo, useState } from "react";
import { HasAccess } from "@/components/common/has-access";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, ArrowDown } from "lucide-react";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import { useUpdatePreferredProfessional } from "@/features/users/hooks/use-update-preferred-professional";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { UserRequest } from "../types/user-request";

interface Props {
  request: UserRequest;
  onResolved: (updated: UserRequest) => void;
}

interface ProfOption {
  id: string;
  name: string;
  picture: string | null;
  specialization: string | null;
  years_of_experience: number | null;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function subtitle(p: ProfOption): string {
  const parts: string[] = [];
  if (p.specialization) parts.push(p.specialization);
  if (typeof p.years_of_experience === "number") {
    parts.push(
      `${p.years_of_experience} yr${p.years_of_experience === 1 ? "" : "s"}`,
    );
  }
  return parts.join(" · ");
}

const ProfCard = ({
  prof,
  size = "md",
}: {
  prof: ProfOption | null;
  size?: "sm" | "md";
}) => {
  if (!prof) {
    return <span className="text-sm text-gray-400">Not assigned</span>;
  }
  const sub = subtitle(prof);
  const avatarSize = size === "sm" ? "h-8 w-8" : "h-10 w-10";
  return (
    <div className="flex items-center gap-3">
      <Avatar className={avatarSize}>
        {prof.picture ? (
          <AvatarImage src={prof.picture} alt={prof.name} />
        ) : null}
        <AvatarFallback className="text-xs">
          {initials(prof.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col min-w-0">
        <span className="text-sm text-gray-900 truncate">{prof.name}</span>
        {sub && (
          <span className="text-xs text-gray-500 truncate">{sub}</span>
        )}
      </div>
    </div>
  );
};

const ProfessionalChangePicker = ({ request, onResolved }: Props) => {
  const queryClient = useQueryClient();
  const { data: profData, isLoading: loadingProfs } = useAllProfessionals();
  const { mutate, isPending } = useUpdatePreferredProfessional();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // "This is a transition by Rocket Health" (RH-748): ticked changes record
  // the row the user's "Transition by RH" tag counts. Reset on every open so a
  // tick from one request never carries over to the next.
  const [isTransition, setIsTransition] = useState(false);

  const bundleUsage = request.bundle_usage;
  const current = bundleUsage?.professional ?? null;
  const userId = bundleUsage?.users?.[0]?.id ?? request.user.id;

  // Filter to ACTIVE professionals only — offboarded ones shouldn't be
  // selectable. The current professional still renders from the request's
  // joined data, so it survives even if it's now inactive.
  const activeProfessionals: ProfOption[] = useMemo(() => {
    return (profData.professionals as any[])
      .filter((p) => p.status === "ACTIVE")
      .map((p) => ({
        id: p.id,
        name: p.name,
        picture: p.picture ?? null,
        specialization: p.specialization ?? null,
        years_of_experience: p.years_of_experience ?? null,
      }));
  }, [profData.professionals]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeProfessionals;
    return activeProfessionals.filter((p) => {
      if (p.name.toLowerCase().includes(q)) return true;
      if (p.specialization?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [activeProfessionals, query]);

  const selectedProf = useMemo(
    () => activeProfessionals.find((p) => p.id === selectedId) ?? null,
    [activeProfessionals, selectedId],
  );

  const sameAsCurrent = !!selectedProf && selectedProf.id === current?.id;
  const canApply = !!selectedProf && !sameAsCurrent && !isPending;

  if (!bundleUsage) return null;

  const handleConfirm = () => {
    if (!selectedProf) return;
    mutate(
      {
        usage_id: bundleUsage.id,
        user_id: userId,
        professional_id: selectedProf.id,
        is_transition: !!current && isTransition,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["user-requests"] });
          setConfirmOpen(false);
          setIsTransition(false);
          onResolved({
            ...request,
            status: "RESOLVED",
            resolved_at: new Date().toISOString(),
            resolution: {
              previous_professional_id: current?.id ?? null,
              previous_professional_name: current?.name ?? null,
              new_professional_id: selectedProf.id,
              new_professional_name: selectedProf.name,
            },
            bundle_usage: {
              ...bundleUsage,
              professional: {
                id: selectedProf.id,
                name: selectedProf.name,
                picture: selectedProf.picture,
                specialization: selectedProf.specialization,
                years_of_experience: selectedProf.years_of_experience,
              },
            },
          });
        },
      },
    );
  };

  return (
    <section>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Change Professional
      </h3>
      <div className="space-y-2">
        <div className="border border-gray-200 rounded-md px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
            Current Professional
          </p>
          <ProfCard prof={current} />
        </div>

        <div className="flex justify-center text-gray-400">
          <ArrowDown className="h-4 w-4" />
        </div>

        <div className="border border-gray-200 rounded-md px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
            New Professional
          </p>
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={popoverOpen}
                className="w-full justify-between font-normal h-auto py-2"
                disabled={loadingProfs}
              >
                <span className={selectedProf ? "min-w-0" : "text-gray-500"}>
                  {selectedProf ? (
                    <ProfCard prof={selectedProf} size="sm" />
                  ) : loadingProfs ? (
                    "Loading…"
                  ) : (
                    "Search professionals…"
                  )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[--radix-popover-trigger-width] p-0 z-[60]"
              align="start"
            >
              <div className="border-b border-gray-200 p-2">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by name or specialization…"
                  className="h-8 text-sm"
                  autoFocus
                />
              </div>
              <div
                className="max-h-72 overflow-y-auto py-1 overscroll-contain"
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                {filtered.length === 0 ? (
                  <p className="px-3 py-4 text-center text-xs text-gray-500">
                    No professionals match
                  </p>
                ) : (
                  filtered.map((p) => {
                    const isSelected = p.id === selectedId;
                    const isCurrent = p.id === current?.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-gray-50 ${
                          isSelected ? "bg-gray-50" : ""
                        }`}
                        onClick={() => {
                          setSelectedId(p.id);
                          setPopoverOpen(false);
                          setQuery("");
                        }}
                      >
                        <div className="flex flex-col min-w-0">
                          <ProfCard prof={p} size="sm" />
                          {isCurrent && (
                            <span className="text-[10px] text-gray-500 ml-11 -mt-0.5">
                              Current
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <Check className="h-4 w-4 text-green-600 shrink-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
          {sameAsCurrent && (
            <p className="text-[11px] text-amber-700 mt-1">
              Already the current professional.
            </p>
          )}
        </div>

        <HasAccess permission="write">
        <Button
          className="w-full"
          disabled={!canApply}
          onClick={() => {
            setIsTransition(false);
            setConfirmOpen(true);
          }}
        >
          Apply Change
        </Button>
        </HasAccess>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm professional change</DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-gray-700 space-y-3">
            <p>
              Change professional for{" "}
              <span className="font-medium">{request.user.full_name}</span>?
            </p>
            <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
              <div className="flex items-center gap-3 px-3 py-2">
                <span className="text-xs text-gray-500 w-12 shrink-0">
                  From
                </span>
                <ProfCard prof={current} size="sm" />
              </div>
              <div className="flex items-center gap-3 px-3 py-2">
                <span className="text-xs text-gray-500 w-12 shrink-0">To</span>
                <ProfCard prof={selectedProf} size="sm" />
              </div>
            </div>
            {/* A first assignment has nobody to transition from, so the tick
                only applies when a current professional exists. */}
            {current && (
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3 text-sm">
                <Checkbox
                  checked={isTransition}
                  onCheckedChange={(checked) =>
                    setIsTransition(checked === true)
                  }
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">
                    This is a transition by Rocket Health
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    Tick when Rocket Health is moving the user, for example
                    because the professional was dismissed or left. Leave it
                    unticked for a change the user asked for. Ticked changes add
                    to the user's "Transition by RH" tag.
                  </span>
                </span>
              </label>
            )}
            <p className="text-xs text-gray-500">
              This will resolve the request and update the user's preferred
              professional.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isPending}>
              {isPending ? "Applying…" : "Confirm Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ProfessionalChangePicker;
